import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";
import { CTA, DemoTag, GlassCard, PageHeader, Shell } from "../components/site/Primitives";

export default function Industries() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [state, setState] = useState("loading");

  const load = async () => {
    setState("loading");
    try {
      const { data } = await api.get("/public/industry-templates");
      setTemplates(data);
      setSelected(data[0]);
      setState("ready");
    } catch (e) {
      setState("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="industries-page">
      <PageHeader
        testId="industries-title"
        eyebrow="Industries"
        title="Configured for how your industry actually books work."
        sub="Each industry template changes services, CRM fields, AI instructions, appointment types, automation workflows and the lead pipeline."
      />

      <section className="py-20">
        <Shell>
          {state === "loading" ? (
            <div data-testid="industries-loading" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-32 animate-pulse rounded-lg border border-elv-line bg-white/[0.02]" />
              ))}
            </div>
          ) : null}

          {state === "error" ? (
            <GlassCard testId="industries-error" hover={false} className="p-8 text-center">
              <Icons.CloudOff className="mx-auto h-6 w-6 text-amber-300" />
              <p className="mt-4 text-sm text-elv-text">We couldn't load the industry templates.</p>
              <p className="mt-2 text-xs text-elv-dim">Check your connection and try again.</p>
              <button
                type="button"
                data-testid="industries-retry"
                onClick={load}
                className="mt-5 rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
              >
                Retry
              </button>
            </GlassCard>
          ) : null}

          {state === "ready" ? (
            <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
              <div className="grid gap-2 sm:grid-cols-2">
                {templates.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`industry-card-${t.key}`}
                    onClick={() => setSelected(t)}
                    className={`glass glass-hover rounded-lg p-4 text-left ${
                      selected?.key === t.key ? "border-elv-green/35 bg-elv-green/[0.06]" : ""
                    }`}
                  >
                    <p className="text-sm font-semibold text-elv-text">{t.name}</p>
                    <p className="mt-2 text-xs leading-relaxed text-elv-dim">{t.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-elv-green">
                      See Your Industry <Icons.ArrowUpRight className="h-3 w-3" />
                    </span>
                  </button>
                ))}
              </div>

              <div className="lg:sticky lg:top-24 lg:self-start">
                <GlassCard testId="industry-detail" hover={false} className="p-6 sm:p-8">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-elv-green">{selected.name}</p>
                    <DemoTag testId="industry-demo-tag">Template preview</DemoTag>
                  </div>
                  <h2 className="mt-4 text-2xl font-semibold text-elv-text">{selected.tagline}</h2>

                  <Block title="Services" items={selected.services} />
                  <Block title="Appointment types" items={selected.appointment_types} />
                  <Block title="CRM fields" items={selected.crm_fields} />
                  <Block title="Automation workflows" items={selected.automations} />

                  <div className="mt-7">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">Lead pipeline</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {selected.pipeline.map((s, i, arr) => (
                        <span key={s} className="flex items-center gap-2">
                          <span className="rounded-sm border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] text-elv-text">
                            {s}
                          </span>
                          {i < arr.length - 1 ? (
                            <Icons.ChevronRight className="h-3 w-3 text-elv-green/70" />
                          ) : null}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-7">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">AI instructions</p>
                    <p className="mt-3 rounded-md border border-white/[0.07] bg-white/[0.02] p-4 font-mono text-[11px] leading-relaxed text-elv-dim">
                      {selected.ai_instructions}
                    </p>
                  </div>

                  {selected.compliance_note ? (
                    <p
                      data-testid="industry-compliance-note"
                      className="mt-6 rounded-md border border-amber-300/25 bg-amber-300/[0.06] p-4 text-[11px] leading-relaxed text-amber-200"
                    >
                      {selected.compliance_note}
                    </p>
                  ) : null}

                  <div className="mt-8">
                    <CTA to="/book-demo" testId="industry-detail-cta">
                      Book a Free AI Demo
                    </CTA>
                  </div>
                </GlassCard>
              </div>
            </div>
          ) : null}
        </Shell>
      </section>
    </div>
  );
}

const Block = ({ title, items }) => (
  <div className="mt-7">
    <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">{title}</p>
    <div className="mt-3 flex flex-wrap gap-2">
      {(items || []).map((i) => (
        <span
          key={i}
          className="rounded-sm border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] text-elv-dim"
        >
          {i}
        </span>
      ))}
    </div>
  </div>
);
